import {ApolloClient, ApolloLink} from '@apollo/client';
import {getEnvironment} from '@sail/utils';
import createInteroperabilityLink from 'src/internal/links/createInteroperabilityLink';
import createDeduplicationLink from 'src/internal/prefetching/createDeduplicationLink';
import createPrefetchLink from 'src/internal/prefetching/createPrefetchLink';
import createRuntimePermissionsCheckLink from 'src/internal/prefetching/createRuntimePermissionsCheckLink';
import {getApolloSharedCaches} from 'src/internal/shared/apollo/getApolloSharedCaches';
import createCache from 'src/public/graphql/createCache';
import createComponentKillswitchLink from 'src/public/links/createComponentKillswitchLink';
import createDirectivesToContextLink from 'src/public/links/createDirectivesToContextLink';
import createKillswitchLink from 'src/public/links/createKillswitchLink';
import createObservabilityLink from 'src/public/links/createObservabilityLink';
import createOverriddenV1ToV2Link from 'src/public/links/createOverriddenV1ToV2Link';
import createTimeoutLink from 'src/public/links/createTimeoutLink';

import type {InMemoryCache} from '@apollo/client';
import type {GraphQlConfig} from 'src/internal/config/types';

export const defaultOptions = {
  watchQuery: {
    fetchPolicy: 'cache-first',
  },
} as const;

const createApolloCache = (graphqlConfig: GraphQlConfig): InMemoryCache => {
  if (graphqlConfig.sharedCache && graphqlConfig.sharedCacheId) {
    throw new Error('Cannot specify both sharedCache and sharedCacheId');
  }

  if (graphqlConfig.sharedCache) {
    return graphqlConfig.sharedCache;
  }

  const sharedApolloCaches = getApolloSharedCaches();

  if (graphqlConfig.sharedCacheId) {
    const existingCache = sharedApolloCaches.get(graphqlConfig.sharedCacheId);

    if (existingCache) {
      return existingCache;
    }
  }

  const cache = createCache(graphqlConfig.schemas);

  if (graphqlConfig.sharedCacheId) {
    sharedApolloCaches.set(graphqlConfig.sharedCacheId, cache);
  }

  return cache;
};

function createApolloLink(graphqlConfig: GraphQlConfig): ApolloLink {
  return ApolloLink.from([
    createDirectivesToContextLink(),
    createObservabilityLink(),
    createInteroperabilityLink(),
    createTimeoutLink(),
    createRuntimePermissionsCheckLink(),
    createPrefetchLink(graphqlConfig.prefetch ?? {}),
    createDeduplicationLink(),
    createKillswitchLink(),
    createComponentKillswitchLink(),
    createOverriddenV1ToV2Link(graphqlConfig),
  ]);
}

export const createApolloClient = (
  graphqlConfig: GraphQlConfig,
): ApolloClient<any> => {
  return new ApolloClient({
    connectToDevTools: getEnvironment() === 'development',
    link: createApolloLink(graphqlConfig),
    cache: createApolloCache(graphqlConfig),
    defaultOptions,
    assumeImmutableResults: true,
    queryDeduplication: false,
  });
};
